import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { useLang } from "@/context/LangContext";

const MIN = 1;
const MAX = 5;

export default function Lightbox({ slides, index, onClose, onIndex, alt = "" }) {
  const { t } = useLang();
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const pinch = useRef(null);
  const stage = useRef(null);
  const count = slides.length;

  const reset = useCallback(() => { setScale(1); setPos({ x: 0, y: 0 }); }, []);
  const go = useCallback((n) => { onIndex((index + n + count) % count); reset(); }, [index, count, onIndex, reset]);
  const zoomTo = (next) => {
    const s = Math.min(MAX, Math.max(MIN, next));
    setScale(s);
    if (s === 1) setPos({ x: 0, y: 0 });
  };

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && count > 1) go(-1);
      if (e.key === "ArrowRight" && count > 1) go(1);
      if (e.key === "+" || e.key === "=") zoomTo(scale + 0.5);
      if (e.key === "-") zoomTo(scale - 0.5);
    };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [onClose, go, count, scale]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = stage.current;
    if (!el) return;
    const onWheel = (e) => { e.preventDefault(); zoomTo(scale + (e.deltaY < 0 ? 0.4 : -0.4)); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [scale]); // eslint-disable-line react-hooks/exhaustive-deps
  const onDoubleClick = () => zoomTo(scale > 1 ? 1 : 2.5);

  const onPointerDown = (e) => {
    if (scale === 1 || e.target.closest("button")) return;
    drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setPos({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
  };
  const onPointerUp = () => (drag.current = null);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) pinch.current = { d: Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY), s: scale };
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinch.current) {
      const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      zoomTo(pinch.current.s * (d / pinch.current.d));
    }
  };
  const onTouchEnd = () => (pinch.current = null);

  const btn = "flex h-11 w-11 items-center justify-center border border-bone/20 bg-ink/70 text-bone backdrop-blur transition-colors hover:border-brass/60 hover:text-brass disabled:opacity-30";

  return createPortal(
    <div data-testid="lightbox" role="dialog" aria-modal="true" className="fixed inset-0 z-[100] flex flex-col bg-ink/95 backdrop-blur-xl" onClick={onClose}>
      <div className="relative z-10 flex items-center justify-between border-b border-bone/10 bg-ink px-4 py-3 sm:px-6" onClick={(e) => e.stopPropagation()}>
        <span data-testid="lightbox-counter" className="font-mono text-[10px] tracking-[0.3em] text-bone-dim">
          {alt}{count > 1 ? ` · ${String(index + 1).padStart(2, "0")} / ${String(count).padStart(2, "0")}` : ""}
        </span>
        <div className="flex items-center gap-2">
          <button data-testid="lightbox-zoom-out" type="button" aria-label={t("lightbox.zoomOut")} onClick={() => zoomTo(scale - 0.5)} disabled={scale <= MIN} className={btn}><ZoomOut size={16} /></button>
          <span data-testid="lightbox-scale" className="w-14 text-center font-mono text-[10px] tracking-[0.2em] text-bone-dim">{Math.round(scale * 100)}%</span>
          <button data-testid="lightbox-zoom-in" type="button" aria-label={t("lightbox.zoomIn")} onClick={() => zoomTo(scale + 0.5)} disabled={scale >= MAX} className={btn}><ZoomIn size={16} /></button>
          <button data-testid="lightbox-reset" type="button" aria-label={t("lightbox.reset")} onClick={reset} className={btn}><Maximize2 size={16} /></button>
          <button data-testid="lightbox-close" type="button" aria-label={t("lightbox.close")} onClick={onClose} className={btn}><X size={18} /></button>
        </div>
      </div>

      <div
        className={`relative flex flex-1 items-center justify-center overflow-hidden ${scale > 1 ? (drag.current ? "cursor-grabbing" : "cursor-grab") : "cursor-zoom-in"}`}
        ref={stage}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={onDoubleClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          data-testid="lightbox-image"
          src={slides[index]}
          alt={alt}
          draggable={false}
          className="max-h-full max-w-full select-none object-contain transition-transform duration-150 ease-out"
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})` }}
        />
        {count > 1 && (
          <>
            <button data-testid="lightbox-prev" type="button" aria-label={t("product.prevPhoto")} onClick={() => go(-1)} className={`${btn} absolute left-3 top-1/2 -translate-y-1/2`}><ChevronLeft size={18} /></button>
            <button data-testid="lightbox-next" type="button" aria-label={t("product.nextPhoto")} onClick={() => go(1)} className={`${btn} absolute right-3 top-1/2 -translate-y-1/2`}><ChevronRight size={18} /></button>
          </>
        )}
      </div>
      <p className="px-6 pb-4 text-center font-mono text-[9px] tracking-[0.25em] text-bone-muted" onClick={(e) => e.stopPropagation()}>{t("lightbox.hint")}</p>
    </div>,
    document.body
  );
}
